import React, { useState, useEffect } from "react";
import { db } from "../firebase";
import { collection, addDoc, getDocs, query, where, orderBy, deleteDoc, doc, updateDoc, serverTimestamp } from "firebase/firestore";

const OnlineClassroom = ({ isAdmin, userEmail, userName }) => {
  const [classes, setClasses] = useState([]);
  const [showScheduleForm, setShowScheduleForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    className: "",
    description: "",
    scheduledTime: "",
    duration: "60",
    batch: "Both",
    meetingId: "",
  });

  // Load classes
  useEffect(() => {
    loadClasses();
  }, []);

  const loadClasses = async () => {
    try {
      setLoading(true);
      const q = isAdmin
        ? query(collection(db, "onlineClasses"), orderBy("scheduledTime", "desc"))
        : query(collection(db, "onlineClasses"), orderBy("scheduledTime", "desc"));

      const snapshot = await getDocs(q);
      const now = Date.now();
      let list = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      if (!isAdmin) {
        // Students: live classes + upcoming ones not yet ended (no composite index needed)
        list = list.filter(c => {
          if (c.isActive) return true;
          if (c.endedAt) return false;
          const start = new Date(c.scheduledTime).getTime();
          return start + (Number(c.duration) || 60) * 60000 >= now;
        });
      }
      setClasses(list);
    } catch (err) {
      console.error("Error loading classes:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleScheduleClass = async (e) => {
    e.preventDefault();
    if (!formData.className || !formData.scheduledTime) {
      alert("Please fill all fields");
      return;
    }

    try {
      // Use custom Meeting ID if provided, otherwise generate one
      let roomId = formData.meetingId.trim();
      if (!roomId) {
        // Generate Google Meet code format: xxx-xxxx-xxx
        const timestamp = Date.now().toString();
        const part1 = timestamp.slice(0, 3);
        const part2 = timestamp.slice(3, 7);
        const part3 = timestamp.slice(7);
        roomId = `${part1}-${part2}-${part3}`;
      }

      await addDoc(collection(db, "onlineClasses"), {
        className: formData.className,
        description: formData.description,
        scheduledTime: new Date(formData.scheduledTime).toISOString(),
        duration: parseInt(formData.duration),
        batch: formData.batch,
        roomId,
        createdBy: userEmail,
        createdByName: userName,
        isActive: false,
        createdAt: serverTimestamp(),
        participants: [],
      });

      alert("✅ Class scheduled successfully!");
      setFormData({ className: "", description: "", scheduledTime: "", duration: "60", batch: "Both", meetingId: "" });
      setShowScheduleForm(false);
      loadClasses();
    } catch (err) {
      alert("Error scheduling class: " + err.message);
    }
  };

  const startClass = async (classId) => {
    try {
      await updateDoc(doc(db, "onlineClasses", classId), {
        isActive: true,
        startedAt: serverTimestamp(),
      });
      loadClasses();
    } catch (err) {
      alert("Error starting class: " + err.message);
    }
  };

  const endClass = async (classId) => {
    try {
      await updateDoc(doc(db, "onlineClasses", classId), {
        isActive: false,
        endedAt: serverTimestamp(),
      });
      loadClasses();
    } catch (err) {
      alert("Error ending class: " + err.message);
    }
  };

  const deleteClass = async (classId) => {
    if (!window.confirm("Are you sure you want to delete this class?")) return;
    try {
      await deleteDoc(doc(db, "onlineClasses", classId));
      loadClasses();
    } catch (err) {
      alert("Error deleting class: " + err.message);
    }
  };


  return (
    <div style={{ padding: "20px", maxWidth: "1200px", margin: "0 auto" }}>
      {/* Header */}
      <div style={{
        background: "linear-gradient(135deg, rgba(66,165,245,0.1) 0%, rgba(76,175,80,0.1) 100%)",
        border: "1.5px solid rgba(76,175,80,0.3)",
        borderRadius: 14,
        padding: "24px",
        marginBottom: 24,
        textAlign: "center"
      }}>
        <h2 style={{ margin: "0 0 12px", color: "#fff", fontSize: "1.8rem", fontWeight: 700 }}>
          🎓 Online Classroom
        </h2>
        <p style={{ margin: 0, color: "rgba(255,255,255,0.65)", fontSize: "0.95rem" }}>
          Schedule and conduct interactive online classes
        </p>
      </div>

      {/* Admin: Schedule Class Button */}
      {isAdmin && (
        <button
          onClick={() => setShowScheduleForm(!showScheduleForm)}
          style={{
            background: "linear-gradient(135deg, #4db8ff 0%, #2196f3 100%)",
            color: "white",
            border: "none",
            padding: "12px 24px",
            borderRadius: "8px",
            cursor: "pointer",
            fontSize: "1rem",
            fontWeight: 600,
            marginBottom: "20px",
          }}
        >
          {showScheduleForm ? "❌ Cancel" : "➕ Schedule New Class"}
        </button>
      )}

      {/* Schedule Form */}
      {showScheduleForm && isAdmin && (
        <div style={{
          background: "rgba(255,255,255,0.03)",
          border: "1.5px solid rgba(255,255,255,0.1)",
          borderRadius: 12,
          padding: "24px",
          marginBottom: 24
        }}>
          <h3 style={{ color: "#4db8ff", marginTop: 0 }}>📋 Schedule New Class</h3>
          <form onSubmit={handleScheduleClass}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "15px", marginBottom: "15px" }}>
              <input
                type="text"
                placeholder="Class Name (e.g., Class 34 - Fluids)"
                value={formData.className}
                onChange={(e) => setFormData({ ...formData, className: e.target.value })}
                style={{
                  background: "rgba(255,255,255,0.05)",
                  border: "1px solid rgba(255,255,255,0.2)",
                  color: "#fff",
                  padding: "10px",
                  borderRadius: "6px",
                }}
              />
              <input
                type="datetime-local"
                value={formData.scheduledTime}
                onChange={(e) => setFormData({ ...formData, scheduledTime: e.target.value })}
                style={{
                  background: "rgba(255,255,255,0.05)",
                  border: "1px solid rgba(255,255,255,0.2)",
                  color: "#fff",
                  padding: "10px",
                  borderRadius: "6px",
                }}
              />
            </div>
            <textarea
              placeholder="Class Description (e.g., Topics to cover)"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              style={{
                width: "100%",
                background: "rgba(255,255,255,0.05)",
                border: "1px solid rgba(255,255,255,0.2)",
                color: "#fff",
                padding: "10px",
                borderRadius: "6px",
                minHeight: "80px",
                marginBottom: "15px",
              }}
            />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "15px", marginBottom: "15px" }}>
              <select
                value={formData.batch}
                onChange={(e) => setFormData({ ...formData, batch: e.target.value })}
                style={{
                  background: "rgba(255,255,255,0.05)",
                  border: "1px solid rgba(255,255,255,0.2)",
                  color: "#fff",
                  padding: "10px",
                  borderRadius: "6px",
                  cursor: "pointer",
                }}
              >
                <option value="C1" style={{ background: "#1a1a2e", color: "#fff" }}>C1 Batch</option>
                <option value="C2" style={{ background: "#1a1a2e", color: "#fff" }}>C2 Batch</option>
                <option value="Both" style={{ background: "#1a1a2e", color: "#fff" }}>Both Batches</option>
              </select>
              <input
                type="number"
                placeholder="Duration (minutes)"
                value={formData.duration}
                onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
                style={{
                  background: "rgba(255,255,255,0.05)",
                  border: "1px solid rgba(255,255,255,0.2)",
                  color: "#fff",
                  padding: "10px",
                  borderRadius: "6px",
                }}
              />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: "10px", marginBottom: "15px", alignItems: "flex-end" }}>
              <input
                type="text"
                placeholder="Meeting ID (leave empty to auto-generate)"
                value={formData.meetingId}
                onChange={(e) => setFormData({ ...formData, meetingId: e.target.value })}
                style={{
                  background: "rgba(255,255,255,0.05)",
                  border: "1px solid rgba(255,255,255,0.2)",
                  color: "#fff",
                  padding: "10px",
                  borderRadius: "6px",
                  fontSize: "0.9rem",
                }}
              />
              <button
                type="button"
                onClick={() => {
                  const timestamp = Date.now().toString();
                  const part1 = timestamp.slice(0, 3);
                  const part2 = timestamp.slice(3, 7);
                  const part3 = timestamp.slice(7);
                  const generatedId = `${part1}-${part2}-${part3}`;
                  setFormData({ ...formData, meetingId: generatedId });
                }}
                style={{
                  background: "rgba(77,184,255,0.3)",
                  color: "#4db8ff",
                  border: "1px solid #4db8ff",
                  padding: "10px 16px",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontSize: "0.85rem",
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                }}
              >
                🔄 Generate
              </button>
            </div>
            <button
              type="submit"
              style={{
                background: "#66bb6a",
                color: "white",
                border: "none",
                padding: "10px 20px",
                borderRadius: "6px",
                cursor: "pointer",
                fontWeight: 600,
              }}
            >
              ✅ Schedule Class
            </button>
          </form>
        </div>
      )}

      {/* Classes List */}
      {loading ? (
        <div style={{ textAlign: "center", color: "rgba(255,255,255,0.6)" }}>Loading classes...</div>
      ) : classes.length === 0 ? (
        <div style={{ textAlign: "center", color: "rgba(255,255,255,0.6)" }}>
          No classes scheduled yet. {isAdmin && "Create one to get started!"}
        </div>
      ) : (
        <div style={{ display: "grid", gap: "15px" }}>
          {classes.map((cls) => (
            <div
              key={cls.id}
              style={{
                background: "rgba(255,255,255,0.03)",
                border: `1.5px solid ${cls.isActive ? "rgba(76,175,80,0.5)" : "rgba(255,255,255,0.1)"}`,
                borderRadius: 12,
                padding: "20px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <div style={{ flex: 1 }}>
                <h4 style={{ margin: "0 0 8px", color: cls.isActive ? "#66bb6a" : "#4db8ff" }}>
                  {cls.isActive && "🔴 LIVE: "} {cls.className}
                </h4>
                <p style={{ margin: "0 0 5px", color: "rgba(255,255,255,0.7)", fontSize: "0.9rem" }}>
                  📅 {new Date(cls.scheduledTime).toLocaleString()}
                </p>
                <p style={{ margin: "0 0 5px", color: "rgba(255,255,255,0.6)", fontSize: "0.85rem" }}>
                  {cls.description}
                </p>
                <p style={{ margin: "0 0 5px", color: "#4db8ff", fontSize: "0.85rem", fontWeight: 600 }}>
                  🔗 Meeting ID: <code style={{ background: "rgba(0,0,0,0.3)", padding: "2px 6px", borderRadius: "4px", fontFamily: "monospace" }}>{cls.roomId}</code>
                </p>
                <p style={{ margin: "0 0 5px", color: "rgba(255,255,255,0.6)", fontSize: "0.8rem" }}>
                  👥 Batch: {cls.batch || "Both"}
                  {isAdmin && (
                    <span style={{ marginLeft: 10, color: "#ce93d8" }}>
                      🎯 {(cls.classIds || []).length ? `Shared with ${cls.classIds.length} classroom(s)` : "Not shared to specific classrooms (Admin → Broadcast → Share to Classes)"}
                    </span>
                  )}
                </p>
                <p style={{ margin: 0, color: "rgba(255,255,255,0.5)", fontSize: "0.85rem" }}>
                  By: {cls.createdByName}
                </p>
              </div>

              <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", justifyContent: "flex-end" }}>
                {cls.isActive ? (
                  <>
                    <button
                      onClick={() => window.open(`https://meet.google.com/${cls.roomId}`, "_blank")}
                      style={{
                        background: "#4db8ff",
                        color: "white",
                        border: "none",
                        padding: "8px 16px",
                        borderRadius: "6px",
                        cursor: "pointer",
                        fontSize: "0.9rem",
                        fontWeight: 600,
                      }}
                    >
                      👁️ Join Class
                    </button>
                    {isAdmin && (
                      <button
                        onClick={() => endClass(cls.id)}
                        style={{
                          background: "#f44336",
                          color: "white",
                          border: "none",
                          padding: "8px 16px",
                          borderRadius: "6px",
                          cursor: "pointer",
                          fontSize: "0.9rem",
                        }}
                      >
                        ⏹️ End Class
                      </button>
                    )}
                  </>
                ) : (
                  <>
                    {isAdmin && (
                      <>
                        <button
                          onClick={() => startClass(cls.id)}
                          style={{
                            background: "#66bb6a",
                            color: "white",
                            border: "none",
                            padding: "8px 16px",
                            borderRadius: "6px",
                            cursor: "pointer",
                            fontSize: "0.9rem",
                          }}
                        >
                          🎬 Start Class
                        </button>
                        <button
                          onClick={() => deleteClass(cls.id)}
                          style={{
                            background: "rgba(255,255,255,0.1)",
                            color: "rgba(255,255,255,0.6)",
                            border: "1px solid rgba(255,255,255,0.2)",
                            padding: "8px 16px",
                            borderRadius: "6px",
                            cursor: "pointer",
                            fontSize: "0.9rem",
                          }}
                        >
                          🗑️ Delete
                        </button>
                      </>
                    )}
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default OnlineClassroom;
